import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { PermissionService } from '../services/permission.service';
import { HomeRouteService } from '../services/home-route.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Route guard that checks RBAC permissions from PermissionService.
 * Only runs when route.data['permissions'] is defined.
 * Routes without data.permissions are unaffected.
 *
 * Usage in routes:
 *   data: { permissions: ['TALLY_ADD'] }
 *   canActivate: [AuthSessionGuard, PermissionGuard]
 *
 * AWAITS PermissionService.syncSessionForGuard() rather than reading whatever snapshot happens
 * to be in memory. This used to assume "AuthSessionGuard always runs first in every route's canActivate
 * array and leaves a fresh snapshot behind" — that assumption is FALSE. Angular runs all guards
 * in a canActivate array CONCURRENTLY (Router.runCanActivate → combineLatest via
 * prioritizedGuardValue; array order picks the winning RESULT, never the execution order). On a
 * cold load (typed URL / refresh / native relaunch) this guard therefore read the BehaviorSubjects'
 * initial values — empty permission set + rbacEnabled=true — denied every gated route and
 * redirected to /unauthorized, cancelling the very navigation whose permission fetch was still
 * in flight. Sidebar navigation was unaffected only because a prior sync had already populated
 * the snapshot. Real bug, fixed 2026-08-03.
 *
 * Awaiting does NOT reintroduce the duplicate-request bug class (view-transport-unit/company-list
 * — see project-double-lazyload-audit memory): syncSessionForGuard() shares its in-flight request,
 * so this joins AuthSessionGuard's single /users/me/permissions call instead of firing a second one.
 * Offline/native semantics are unchanged and now actually honoured here — the shared sync resolves
 * to the Dexie-cached snapshot ('cached') offline, so this guard enforces real cached permissions
 * instead of an empty set (see project-permission-guard-offline-denial-bug memory); 'never-synced'
 * is still AuthSessionGuard's call alone, and its false wins by array position.
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionGuard implements CanActivate {

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private homeRoute: HomeRouteService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state?: RouterStateSnapshot): Observable<boolean> {
    const required: string[] = route.data?.['permissions'] ?? [];

    if (!required.length) {
      return of(true);
    }

    // data.permissionsExemptChildPaths (2026-09-17): child pages of this route that carry their OWN
    // guard and must not need the parent's permissions. Only /new-tallysheet uses it, for
    // 'proc-output' (a mill operator's own output tally - ProcessingOutputGuard does the real check).
    const exempt: string[] = route.data?.['permissionsExemptChildPaths'] ?? [];
    const parentPath = route.routeConfig?.path;
    if (exempt.length && parentPath && state?.url) {
      const urlPath = state.url.split('?')[0].split('#')[0];
      if (exempt.some(child => urlPath === `/${parentPath}/${child}`)) {
        return of(true);
      }
    }

    // Join (never duplicate) the same session sync AuthSessionGuard starts on this navigation, and only
    // then read the snapshot — pre-sync BehaviorSubject defaults are not a valid basis for a
    // deny decision. See the class doc above for why awaiting is required and safe.
    return this.permissionService.syncSessionForGuard().pipe(
      map((result) => {
        // Native + offline + this device has NEVER completed a live sync: there is no trustworthy
        // permission data to judge against, and AuthSessionGuard is already force-logging-out and
        // navigating to /login for exactly this outcome. Deny (AuthSessionGuard's false wins by array
        // position anyway) but do NOT fire check()'s /unauthorized navigation — that would race
        // the logout redirect and could strand the user on /unauthorized instead of /login.
        if (result.outcome === 'never-synced') return false;
        // RBAC disabled for this tenant → allow, AuthSessionGuard already verified auth/subscription.
        // RBAC enabled → enforce for real, even if this user currently has zero grants — an
        // empty set here no longer means "disabled," it means "denied." AuthSessionGuard has already
        // refused to let navigation reach this guard at all if it couldn't establish a
        // trustworthy snapshot (see syncSessionForGuard()'s 'never-synced' outcome), so the
        // snapshot here is always either a genuinely live result or a legitimately-cached
        // last-known-good one.
        if (!this.permissionService.isRbacEnabledSnapshot()) return true;
        return this.check(required, this.permissionService.getSnapshot(), route, state);
      }),
    );
  }

  private check(required: string[], perms: Set<string>, route: ActivatedRouteSnapshot, state?: RouterStateSnapshot): boolean {
    const granted = required.some(p => perms.has(p));
    if (!granted) this.redirectDenied(route, state);
    return granted;
  }

  /**
   * Smart landing (SMART_LANDING_PLAN.md, 2026-09-28). A refused AUTOMATIC landing (the dashboard
   * right after login / a company switch) quietly goes to the first page the user can open — or to
   * the welcome screen when there is none — instead of greeting them with "Access restricted".
   * A page opened on purpose (link, bookmark, typed URL) still shows the restricted page, which
   * gets the asked-for page so "Request access" can name it. No loop: the home page is chosen by
   * the same route rules this guard checks, so it is never a page that would be refused.
   */
  private redirectDenied(route: ActivatedRouteSnapshot, state?: RouterStateSnapshot): void {
    const target = (state?.url ?? '').split('#')[0];
    if (HomeRouteService.isLandingPath(route.routeConfig?.path)) {
      this.homeRoute.resolveHome().subscribe(home => {
        if (home && home !== target.split('?')[0]) {
          this.router.navigateByUrl(home, { replaceUrl: true });
        } else {
          this.router.navigate(['/unauthorized'], { queryParams: { welcome: 1 }, replaceUrl: true });
        }
      });
      return;
    }
    this.router.navigate(['/unauthorized'], { queryParams: { page: target || null } });
  }
}
